import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import NavBar from "@/components/NavBar";
import api, { formatErr } from "@/lib/api";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setErr("Passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      // Redirect after a moment so user sees the success state
      setTimeout(() => nav("/login", { replace: true }), 2000);
    } catch (e) {
      setErr(formatErr(e));
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="App" data-testid="page-reset-password">
        <NavBar />
        <section className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
          <div className="aa-card p-7" data-testid="reset-no-token">
            <div className="aa-eyebrow mb-2">Missing reset link</div>
            <p className="text-[var(--aa-text-2)]">This page is opened via the link in your reset email. If you landed here directly, please request a new link.</p>
            <Link to="/forgot-password" className="aa-cta-secondary mt-5 inline-flex" data-testid="reset-request-new">Request a reset link</Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="App" data-testid="page-reset-password">
      <NavBar />
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="aa-eyebrow mb-4">Set a new password</div>
        <h1 className="font-display text-4xl lg:text-5xl leading-tight">
          Almost <span className="serif-italic">there</span>.
        </h1>
        <p className="mt-5 text-[var(--aa-text-2)] max-w-xl">Choose a new password for your Ageless Alchemy account. After this, you&apos;ll be redirected to sign in.</p>

        {done ? (
          <div className="aa-card aa-card-sage p-7 mt-10" data-testid="reset-success">
            <div className="aa-eyebrow mb-2">Password updated ✓</div>
            <p className="text-[var(--aa-text-2)]">Redirecting you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="aa-card p-8 mt-10 space-y-5 max-w-md">
            <div>
              <label className="aa-eyebrow block mb-2">New password</label>
              <input
                data-testid="reset-password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent border-b border-[var(--aa-elevated)] focus:border-[var(--aa-text)] outline-none py-3 text-base"
              />
              <div className="text-xs text-[var(--aa-text-3)] mt-2">At least 6 characters.</div>
            </div>
            <div>
              <label className="aa-eyebrow block mb-2">Confirm new password</label>
              <input
                data-testid="reset-confirm"
                type="password"
                required
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full bg-transparent border-b border-[var(--aa-elevated)] focus:border-[var(--aa-text)] outline-none py-3 text-base"
              />
            </div>
            {err && <div data-testid="reset-error" className="text-sm text-[var(--aa-terra)]">{err}</div>}
            <button data-testid="reset-submit" disabled={loading} className="aa-cta-primary w-full">
              {loading ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
